/**
 * TS Select — accessible custom dropdown
 * Initializes all [data-ts-select] roots on the page.
 */
(function () {
  function initSelect(root) {
    if (root.dataset.tsSelectReady === 'true') return;
    root.dataset.tsSelectReady = 'true';

    const trigger = root.querySelector('[data-ts-select-trigger]');
    const list = root.querySelector('[data-ts-select-list]');
    const native = root.querySelector('[data-ts-select-native]');
    const valueEl = root.querySelector('[data-ts-select-value]');
    const options = Array.from(root.querySelectorAll('[data-ts-select-option]'));
    const continueEl = document.querySelector(
      `[data-ts-select-continue][data-ts-select-continue-for="${root.id}"]`
    );

    if (!trigger || !list || options.length === 0) return;

    let selectedIndex = options.findIndex((opt) => opt.classList.contains('is-selected'));
    if (selectedIndex < 0) selectedIndex = 0;

    function getUrl(index) {
      const opt = options[index];
      return opt ? opt.dataset.url || '' : '';
    }

    function syncContinue() {
      if (!continueEl) return;
      const url = getUrl(selectedIndex);
      if (url) {
        continueEl.setAttribute('href', url);
        continueEl.removeAttribute('aria-disabled');
        continueEl.classList.remove('is-disabled');
      } else {
        continueEl.setAttribute('href', '#');
        continueEl.setAttribute('aria-disabled', 'true');
        continueEl.classList.add('is-disabled');
      }
    }

    function setSelected(index, shouldClose) {
      if (index < 0 || index >= options.length) return;
      selectedIndex = index;

      options.forEach((opt, i) => {
        const isSelected = i === selectedIndex;
        opt.classList.toggle('is-selected', isSelected);
        opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        opt.classList.remove('is-focused');
      });

      const selected = options[selectedIndex];
      const label = selected.dataset.label || selected.textContent.trim();
      if (valueEl) valueEl.textContent = label;
      if (native) native.value = selected.dataset.value || String(selectedIndex);

      root.dispatchEvent(
        new CustomEvent('ts-select:change', {
          bubbles: true,
          detail: {
            index: selectedIndex,
            label,
            url: getUrl(selectedIndex),
            value: selected.dataset.value || String(selectedIndex),
          },
        })
      );

      syncContinue();
      if (shouldClose) close();
    }

    function open() {
      root.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      list.hidden = false;
      options[selectedIndex]?.classList.add('is-focused');
    }

    function close() {
      root.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      list.hidden = true;
      options.forEach((opt) => opt.classList.remove('is-focused'));
    }

    function toggle() {
      if (root.classList.contains('is-open')) close();
      else open();
    }

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      toggle();
    });

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!root.classList.contains('is-open')) open();
        else {
          const next = Math.min(selectedIndex + 1, options.length - 1);
          setSelected(next, false);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!root.classList.contains('is-open')) open();
        else {
          const prev = Math.max(selectedIndex - 1, 0);
          setSelected(prev, false);
        }
      } else if (e.key === 'Escape') {
        close();
      }
    });

    options.forEach((opt, index) => {
      opt.addEventListener('click', (e) => {
        e.preventDefault();
        setSelected(index, true);
      });

      opt.addEventListener('mouseenter', () => {
        options.forEach((o) => o.classList.remove('is-focused'));
        opt.classList.add('is-focused');
      });
    });

    if (native) {
      native.addEventListener('change', () => {
        const idx = options.findIndex((o) => (o.dataset.value || '') === native.value);
        setSelected(idx >= 0 ? idx : parseInt(native.value, 10) || 0, true);
      });
    }

    document.addEventListener('click', (e) => {
      if (!root.contains(e.target)) close();
    });

    setSelected(selectedIndex, false);
  }

  function initAll() {
    document.querySelectorAll('[data-ts-select]').forEach(initSelect);
  }

  if (!window.__tsSelectListenersBound) {
    window.__tsSelectListenersBound = true;
    document.addEventListener('DOMContentLoaded', initAll);
    document.addEventListener('shopify:section:load', initAll);
  }

  initAll();
})();
